import Axios from 'axios';
import { classifyResponseError, getCurrentCompanyId, isSwitchingCompany } from './companyContext';
import { onCompanyContextMismatch, onStoreContextMismatch } from './contextMismatch';

/** 呼び出し元に返す、解決しない Promise（各画面の catch を走らせない） */
const hold = () => new Promise<never>(() => { /* 読み直すまで待たせる */ });

const isDev = process.env.NODE_ENV === 'development';
// 開発時は Next(同一オリジン) に相対パスで投げて rewrites で nginx/Laravel へ中継し、CORS/クッキー制約を回避する
const baseURL = isDev ? '' : (process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:8080');

const axios = Axios.create({
    baseURL,
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        // Content-Typeを削除してFormDataアップロードを許可
    },
    withCredentials: true,
    withXSRFToken: true,
    timeout: 30000,
});

// リクエストインターセプター - 認証トークンを自動追加
axios.interceptors.request.use(
    (config) => {
        // ブラウザ環境でのみ実行
        if (typeof window !== 'undefined') {
            const token = localStorage.getItem('auth_token');
            if (token) {
                config.headers['Authorization'] = `Bearer ${token}`;
            }

            // 選択中の店舗IDをヘッダーに付与（店舗スコープAPI用）
            const selectedStoreId = localStorage.getItem('selected_store_id');
            if (selectedStoreId) {
                config.headers['X-Store-ID'] = selectedStoreId;
            }

            // このタブが受け取った「今の会社」（2026-10-17）。サーバはトークンの会社と照合し、違えば 409 を返す。
            // 🔴 会社はタブのメモリから送る（localStorage から読むと、別のタブの切り替えと同時に変わって照合にならない）
            const companyId = getCurrentCompanyId();
            if (companyId) {
                config.headers['X-Company-ID'] = String(companyId);
            }
        }
        
        // FormDataの場合はContent-Typeを設定しない（ブラウザが自動設定）
        if (!(config.data instanceof FormData)) {
            config.headers['Content-Type'] = 'application/json';
        }
        
        return config;
    },
    (error) => Promise.reject(error)
);

// レスポンスインターセプター - エラー処理
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        // 🔴 会社・店の食い違い（409）・切り替えの最中・古いトークンの 401 の扱いは classifyResponseError で決める（2026-10-17。判定はテストできる所に置く）。
        //    - 409 は**先頭で**受けて読み直し、呼び出し元には解決しない Promise を返す（画面の 409 の処理〈退勤の「すでに退勤済みです」など〉に渡さない）
        //    - 切り替えの要求そのもの（`__companySwitch`）は握らない（失敗したら呼び出し元が理由を出す）
        //    - 401 でトークンを消すのは、その要求が送ったトークン＝今のトークンのときだけ
        const sentToken = String(error.config?.headers?.Authorization ?? '').replace(/^Bearer\s+/i, '');
        const currentToken = typeof window !== 'undefined' ? (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })() : null;
        const action = classifyResponseError({
            status: error.response?.status,
            data: error.response?.data,
            isSwitchRequest: !!error.config?.__companySwitch,
            switching: isSwitchingCompany(),
            sentToken,
            currentToken,
        });
        if (action === 'hold') return hold();
        if (action === 'reload-company') { onCompanyContextMismatch(); return hold(); }
        if (action === 'reload-store') { onStoreContextMismatch(); return hold(); }

        // 422（バリデーション）はそのまま返す（呼び出し側で処理）
        if (error.response?.status === 422) {
            return Promise.reject(error);
        }

        // 401エラー（認証エラー）の処理（古いトークンの 401 は action = 'pass' で、ここに入らない）
        if (error.response?.status === 401 && action === 'clear-token') {
            if (typeof window !== 'undefined') {
                // トークンを削除（localStorageとクッキーの両方）
                localStorage.removeItem('auth_token');
                document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
                delete axios.defaults.headers.common['Authorization'];
                
                const currentPath = window.location.pathname;
                
                // ログインページ以外にいる場合はログインページにリダイレクト
                // グローバルインターセプター内のため router は使わず、現在タブ限定で置き換え
                if (!currentPath.includes('/login') && !currentPath.includes('/register')) {
                    const redirectUrl = `/login?redirect=${encodeURIComponent(currentPath)}`;
                    window.location.replace(redirectUrl);
                }
            }
        }

        // 403エラー（認可エラー）の処理
        // 呼び出し側でトースト表示するため、ここでは何もせずエラーをそのまま返す
        // （/access-denied へのリダイレクトは行わない）
        if (error.response?.status === 403) {
            console.warn('Forbidden:', {
                status: 403,
                message: error.response?.data?.message || 'Access denied',
                url: error.config?.url
            });
        }

        // 500エラー（サーバーエラー）の処理
        if (error.response?.status >= 500) {
            console.warn('Server Error:', {
                status: error.response?.status,
                data: error.response?.data,
                url: error.config?.url
            });
            // エラー報告やトースト通知などを実装可能
        }

        // 中断されたリクエスト（AbortController によるクリーンアップ等）は
        // 正常な挙動なのでログしない。これがないと再レンダーのたびに
        // 「Network Error: canceled」がコンソールを埋め尽くす。
        const isCanceled =
            Axios.isCancel?.(error) ||
            error.code === 'ERR_CANCELED' ||
            error.message === 'canceled';

        // ネットワークエラーの処理（ログだけ）
        if (!error.response && !isCanceled) {
            // タイムアウトエラーの詳細処理
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                console.warn('Timeout Error:', {
                    message: error.message,
                    code: error.code,
                    url: error.config?.url,
                    timeout: error.config?.timeout
                });
            } else {
                console.warn('Network Error:', { message: error.message, url: error.config?.url });
            }
        }

        // エラーをrejectする（ただし、422エラーは特別に処理済み）
        return Promise.reject(error);
    }
);

export default axios; 
